/**
 * Bluedot Location Permission Handler
 *
 * Manages the critical location permission flow for Bluedot SDK.
 *
 * IMPORTANT: Location permission can only be requested ONCE in web environment.
 * This module ensures proper sequencing:
 * 1. Request browser geolocation permission first
 * 2. Only if granted, enable Bluedot location
 *
 * This pattern is required because Bluedot's location request will silently
 * fail if browser permission wasn't granted first in the web context.
 */

import bluedotService from "../services/bluedot";
import bluedotConfig from "../config/bluedot";

// Permission state
let permissionState = {
  requested: false,
  granted: false,
  denied: false,
  error: null
};

/**
 * Get current permission state
 *
 * @returns {Object}
 */
export const getPermissionState = () => {
  return { ...permissionState };
};

/**
 * Reset permission state (for testing)
 */
export const resetPermissionState = () => {
  permissionState = {
    requested: false,
    granted: false,
    denied: false,
    error: null
  };
};

/**
 * Request browser geolocation permission
 * This must be called before enabling Bluedot location
 *
 * @returns {Promise<boolean>}
 */
export const requestBrowserLocationPermission = async () => {
  if (typeof navigator === "undefined" || !navigator.permissions) {
    permissionState.error = "Permissions API not available";
    return false;
  }

  if (permissionState.requested) {
    return permissionState.granted;
  }

  try {
    // Check current permission status first
    const result = await navigator.permissions.query({ name: "geolocation" });

    if (result.state === "granted") {
      permissionState.requested = true;
      permissionState.granted = true;
      return true;
    }

    if (result.state === "denied") {
      permissionState.requested = true;
      permissionState.denied = true;
      return false;
    }

    // State is 'prompt' - need to request
    return await requestGeolocation();
  } catch (error) {
    // Permissions API might not support geolocation query
    // Fall back to direct request
    return await requestGeolocation();
  }
};

/**
 * Direct geolocation request
 *
 * @returns {Promise<boolean>}
 */
const requestGeolocation = async () => {
  return new Promise(resolve => {
    if (typeof navigator === "undefined" || !navigator.geolocation) {
      permissionState.error = "Geolocation API not available";
      resolve(false);
      return;
    }

    navigator.geolocation.getCurrentPosition(
      position => {
        permissionState.requested = true;
        permissionState.granted = true;
        resolve(true);
      },
      error => {
        permissionState.requested = true;
        permissionState.denied = true;
        permissionState.error = error.message;
        resolve(false);
      },
      {
        enableHighAccuracy: true,
        timeout: 10000,
        maximumAge: 0
      }
    );
  });
};

/**
 * Request Bluedot location after browser permission
 * IMPORTANT: This should only be called after browser permission is granted
 *
 * @returns {Promise<boolean>}
 */
export const requestBluedotLocation = async () => {
  if (!bluedotConfig.enabled) {
    return false;
  }

  // First ensure browser permission is granted
  const browserPermissionGranted = await requestBrowserLocationPermission();

  if (!browserPermissionGranted) {
    return false;
  }

  // Now enable Bluedot location
  const result = await bluedotService.enableLocation();

  return result;
};

/**
 * Convenience function to request location with user interaction
 * Call this on user action (button click, etc.)
 *
 * @returns {Promise<{success: boolean, message: string}>}
 */
export const requestLocationWithInteraction = async () => {
  if (!bluedotConfig.enabled) {
    return {
      success: false,
      message: "Bluedot is disabled"
    };
  }

  if (permissionState.granted) {
    return {
      success: true,
      message: "Location already enabled"
    };
  }

  try {
    const result = await requestBluedotLocation();

    if (result) {
      return {
        success: true,
        message: "Location enabled successfully"
      };
    } else {
      return {
        success: false,
        message: "Location permission denied"
      };
    }
  } catch (error) {
    return {
      success: false,
      message: error.message || "Failed to enable location"
    };
  }
};

/**
 * Check if location permission has been granted
 *
 * @returns {boolean}
 */
export const hasLocationPermission = () => {
  return permissionState.granted;
};

/**
 * Check if location was denied by user
 *
 * @returns {boolean}
 */
export const wasLocationDenied = () => {
  return permissionState.denied;
};

export default {
  getPermissionState,
  resetPermissionState,
  requestBrowserLocationPermission,
  requestBluedotLocation,
  requestLocationWithInteraction,
  hasLocationPermission,
  wasLocationDenied
};
